import React, { useCallback, useRef, useState } from "react";
import styled, { css } from "styled-components";
import isEqual from "lodash/isEqual";
import { SplitButton } from "components/bootstrap";
import { Icon, TextOverflowEllipsis } from "components/common";
import SearchFilterActionsPopover from "search-filter/components/search-filter-bar/SearchFilterActionsPopover";
import { REFERENCED_USER_SEARCH_FILTER_TYPE } from "search-filter/constants";
import SearchFilterEditPopover from "search-filter/components/search-filter-bar/SearchFilterEditPopover";
import UserFilterShareModal from "search-filter/components/UserFilterShareModal";
import useSearchFiltersFormState from "search-filter/hooks/useSearchFiltersFormState";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSearchFiltersFormActions from "../../hooks/useSearchFiltersFormActions";
const HelperSpan = styled.span`
  font-size: 70%;
  text-align: right;
`;
const InlineDiv = styled.div(
  ({ $isDisabled }) => css`
    text-decoration: ${$isDisabled ? "line-through" : "initial"};
    display: flex;
    gap: 5px;
    align-items: center;
  `
);
const ButtonTitle = styled.div(
  ({ $isDisabled }) => css`
    text-decoration: ${$isDisabled ? "line-through" : "initial"};
    display: flex;
    align-items: center;
    gap: 5px;
  `
);
const StyledSplitButton = styled(SplitButton)`
  && {
    padding: 0;
    height: auto;
    align-items: flex-start;
  }

  && .mantine-Button-label {
    align-items: flex-start;
  }

  &:hover {
    text-decoration: none;
    background: transparent;
  }

  &:focus {
    text-decoration: none;
  }
`;
const PlaceholderPopoverTarget = styled.div`
  width: 100%;
`;
const StyledTextOverflowEllipsis = styled(TextOverflowEllipsis)`
  max-width: 125px;
  flex-grow: 1;
`;
const FilterReferenceLabel = ({ filterType, filterId }) => {
  const currentUser = useCurrentUser();
  if (filterType !== REFERENCED_USER_SEARCH_FILTER_TYPE) {
    return null;
  }
  const userIsOwner = isPermitted(currentUser.permissions, [`search_filters:delete:${filterId}`]);
  return /* @__PURE__ */ React.createElement(HelperSpan, null, userIsOwner ? 'from "My Filters"' : "shared with me");
};
const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const SelectedSearchFilter = ({
  filter,
  filter: { id, title, queryString, type, frontendId, negation, description, disabled }
}) => {
  const { updateFilter } = useSearchFiltersFormActions();
  const filterContainerRef = useRef();
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showEditPopover, setShowEditPopover] = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const filterTitle = title || queryString;
  const hoverTitle = `${title ? `Title: ${title}. ` : ""}Query: ${queryString}.${description ? ` Description: ${description}.` : ""}`;
  const selectedFilters = useSearchFiltersFormState();
  const sendTelemetry = useSendTelemetry();
  const toggleShowFilterMenu = useCallback(() => {
    setShowFilterMenu((cur) => !cur);
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_MENU_TOGGLED, {
      app_pathname: "search",
      app_section: "search-filter"
    });
  }, [sendTelemetry]);
  const toggleDisableFilter = useCallback(
    (e) => {
      e.preventDefault();
      updateFilter(frontendId, { ...filter, disabled: !disabled });
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_DISABLED_TOGGLED, {
        app_pathname: "search",
        app_section: "search-filter",
        app_action_value: "filter-disable",
        event_details: {
          disabled
        }
      });
    },
    [disabled, filter, frontendId, sendTelemetry, updateFilter]
  );
  const toggleShowSharePopover = useCallback(() => {
    setShowSharePopover((cur) => !cur);
    if (!showSharePopover) {
      toggleShowFilterMenu();
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_SHARE_CLICKED, {
      app_pathname: "search",
      app_section: "search-filter",
      app_action_value: "filter-share"
    });
  }, [sendTelemetry, showSharePopover, toggleShowFilterMenu]);
  const toggleShowEditPopover = useCallback(() => {
    setShowEditPopover((cur) => !cur);
    if (!showEditPopover) {
      toggleShowFilterMenu();
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_EDIT_CLICKED, {
      app_pathname: "search",
      app_section: "search-filter",
      app_action_value: "filter-edit"
    });
  }, [sendTelemetry, showEditPopover, toggleShowFilterMenu]);
  const updateFilterInCurrentSearch = useCallback(
    (newFilter) => {
      const previousFilter = selectedFilters.get(newFilter.frontendId);
      if (!isEqual(newFilter, previousFilter)) {
        updateFilter(newFilter.frontendId, newFilter);
      }
      toggleShowEditPopover();
    },
    [selectedFilters, toggleShowEditPopover, updateFilter]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StyledSplitButton,
    {
      bsSize: "xsmall",
      bsStyle: "transparent",
      ref: filterContainerRef,
      open: showFilterMenu,
      onMenuChange: setShowFilterMenu,
      id: `filter-menu-popover-split-button-${frontendId}`,
      width: 240,
      title: /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(InlineDiv, { onClick: toggleDisableFilter }, /* @__PURE__ */ React.createElement(Icon, { name: disabled ? "square" : "check_box" }), /* @__PURE__ */ React.createElement(ButtonTitle, { $isDisabled: disabled, "data-testid": "selected-filter-title" }, negation && /* @__PURE__ */ React.createElement("b", null, "NOT"), /* @__PURE__ */ React.createElement(StyledTextOverflowEllipsis, { titleOverride: hoverTitle }, filterTitle))), /* @__PURE__ */ React.createElement(FilterReferenceLabel, { filterType: type, filterId: id })),
      dataTestId: `selected-filter-${frontendId}`
    },
    /* @__PURE__ */ React.createElement(
      SearchFilterActionsPopover,
      {
        filter,
        onEditClick: toggleShowEditPopover,
        onShareClick: toggleShowSharePopover,
        onItemClick: toggleShowFilterMenu
      }
    )
  ), showEditPopover && /* @__PURE__ */ React.createElement(SearchFilterEditPopover, { onSave: updateFilterInCurrentSearch, onCancel: toggleShowEditPopover, filter }, /* @__PURE__ */ React.createElement(PlaceholderPopoverTarget, null)), showSharePopover && /* @__PURE__ */ React.createElement(UserFilterShareModal, { filter, toggleShareModalOpen: toggleShowSharePopover }));
};
export default SelectedSearchFilter;
